import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import PortalSnackbar from '../components/Common/PortalSnackbar';

const PlatformAlertsContext = createContext(null);

/**
 * App-wide alerts fixed to the top (portal + Snackbar), consistent with PWAInstallPrompt offset.
 */
export function PlatformAlertsProvider({ children }) {
  const [payload, setPayload] = useState(null);

  const hidePlatformAlert = useCallback(() => {
    setPayload(null);
  }, []);

  const showPlatformAlert = useCallback(
    (message, severity = 'info', options = {}) => {
      if (message == null || message === '') return;
      const autoHide =
        options.autoHideDuration ??
        (severity === 'error' ? 8000 : 6000);
      setPayload({
        message: String(message),
        severity,
        autoHideDuration: autoHide,
      });
    },
    []
  );

  const showError = useCallback((m, o) => showPlatformAlert(m, 'error', o), [showPlatformAlert]);
  const showSuccess = useCallback((m, o) => showPlatformAlert(m, 'success', o), [showPlatformAlert]);
  const showWarning = useCallback((m, o) => showPlatformAlert(m, 'warning', o), [showPlatformAlert]);
  const showInfo = useCallback((m, o) => showPlatformAlert(m, 'info', o), [showPlatformAlert]);

  const value = useMemo(
    () => ({
      showPlatformAlert,
      hidePlatformAlert,
      showError,
      showSuccess,
      showWarning,
      showInfo,
    }),
    [showPlatformAlert, hidePlatformAlert, showError, showSuccess, showWarning, showInfo]
  );

  return (
    <PlatformAlertsContext.Provider value={value}>
      {children}
      <PortalSnackbar
        open={Boolean(payload)}
        autoHideDuration={payload?.autoHideDuration ?? 6000}
        onClose={hidePlatformAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: { xs: 72, sm: 80 },
        }}
      >
        {payload ? (
          <Alert severity={payload.severity} onClose={hidePlatformAlert} sx={{ width: '100%' }}>
            {payload.message}
          </Alert>
        ) : null}
      </PortalSnackbar>
    </PlatformAlertsContext.Provider>
  );
}

export function usePlatformAlerts() {
  const ctx = useContext(PlatformAlertsContext);
  if (!ctx) {
    throw new Error('usePlatformAlerts must be used within PlatformAlertsProvider');
  }
  return ctx;
}
